<template lang="pug">
.page.page-about.about-v2(ref="aboutRoot", :class="{ 'about-zh': isZh }")
  header.portrait-opening
    .portrait-identity
      p.about-kicker {{ isZh ? '藝術家 · 台北' : 'Artist · Taipei' }}
      h1
        span.name-en Che-Yu Wu
        | {{ ' ' }}
        span.name-zh(lang="zh-Hant") 吳哲宇
      p.identity-line {{ bio.identity }}
      dl.at-a-glance(:aria-label="isZh ? '速覽' : 'At a glance'")
        .glance-row(v-for="fact in bio.facts", :key="fact.label")
          dt {{ fact.label }}
          dd
            template(v-for="(item, i) in fact.items", :key="i")
              span.glance-item
                router-link(v-if="item.to", :to="localizedPath(item.to)") {{ item.text }}
                template(v-else) {{ item.text }}
              span.glance-sep(v-if="i < fact.items.length - 1") {{ isZh ? '、' : ' · ' }}
      .opening-links
        a.about-text-link(href="#about-biography") {{ isZh ? '閱讀完整簡介' : 'Read the biography' }} ↓
        router-link.about-text-link(:to="localizedPath('/work')") {{ $t('about.heroCta1') }} ↗︎
        router-link.about-text-link(:to="localizedPath('/cv')") {{ isZh ? '完整履歷' : 'Full CV' }} ↗︎
        a.about-text-link(href="#about-contact") {{ $t('about.heroCta2') }} ↓
    figure.portrait-image
      img(src="/static/img/CheYu-0401-crop.jpg", :alt="isZh ? '吳哲宇的藝術家肖像' : 'Portrait of artist Che-Yu Wu'", width="900", height="1290", fetchpriority="high")
    blockquote.opening-statement
      p {{ $t('about.quote') }}
      cite {{ $t('about.quoteSource') }}

  .about-chapter-nav(role="navigation", :aria-label="isZh ? '本頁章節' : 'On this page'")
    a(v-for="(chapter, i) in chapters", :key="chapter.id", :href="'#' + chapter.id")
      span {{ String(i + 1).padStart(2, '0') }}
      | {{ chapter.label }}

  //- 01 藝術家簡介：本頁主體。全文直接展開（2026-10-04 哲宇：「最最最重要的藝術家簡介是收合的，很不滿意」）。
  //- 文字住在 src/data/about-biography.json，每一句的出處見該檔與 Obsidian 歸檔；引句只用查得到原話的。
  section.about-chapter#about-biography(aria-labelledby="biography-title", data-about-reveal)
    .chapter-heading
      span.chapter-number 01
      h2#biography-title {{ isZh ? '藝術家簡介' : 'Biography' }}
    .biography-layout
      aside.biography-margin
        //- 用 div＋role：全站 nav 元素有 flex 置中的樣式（App.vue），會把目錄擠成置中
        .bio-toc(role="navigation", :aria-label="isZh ? '簡介段落' : 'Biography sections'")
          p.about-kicker {{ isZh ? '七個段落' : 'Seven parts' }}
          a(v-for="chapter in bio.chapters", :key="chapter.id", :href="'#' + chapter.id", :class="{ 'is-current': currentChapter === chapter.id }", :aria-current="currentChapter === chapter.id ? 'location' : null")
            span.toc-years {{ chapter.years }}
            span.toc-title {{ chapter.title }}
        .bio-copy-actions
          p.about-kicker {{ isZh ? '引用' : 'Quote this text' }}
          button.about-text-link(type="button", @click="copy('short')") {{ copied === 'short' ? (isZh ? '已複製短版' : 'Short version copied') : (isZh ? '複製短版簡介' : 'Copy the short biography') }}
          button.about-text-link(type="button", @click="copy('long')") {{ copied === 'long' ? (isZh ? '已複製完整簡介' : 'Full biography copied') : (isZh ? '複製完整簡介' : 'Copy the full biography') }}
          a.about-text-link(href="#about-press-kit") {{ isZh ? '更多媒體資料' : 'Press materials' }} ↓
      .biography-reading
        p.bio-lead {{ bio.short }}
        article.bio-chapter(v-for="chapter in bio.chapters", :key="chapter.id", :id="chapter.id", :aria-labelledby="chapter.id + '-title'")
          header.bio-chapter-head
            span.about-kicker {{ chapter.years }}
            h3(:id="chapter.id + '-title'") {{ chapter.title }}
          template(v-for="(block, bi) in chapter.blocks", :key="bi")
            p(v-if="block.p")
              template(v-for="(seg, si) in segments(block.p)", :key="si")
                router-link(v-if="seg.to", :to="seg.to") {{ seg.text }}
                a(v-else-if="seg.href", :href="seg.href", :target="seg.external ? '_blank' : null", :rel="seg.external ? 'noopener noreferrer' : null") {{ seg.text }}
                template(v-else) {{ seg.text }}
            blockquote.bio-quote(v-else-if="block.quote")
              p {{ block.quote }}
              cite
                router-link(v-if="block.to", :to="localizedPath(block.to)") {{ block.cite }}
                template(v-else) {{ block.cite }}
            .bio-figures(v-else-if="block.photos", :class="'count-' + block.photos.length")
              figure(v-for="id in block.photos", :key="id")
                button(type="button", :aria-label="(isZh ? '放大照片：' : 'Enlarge photograph: ') + photoCaption(id)", @click="openPhoto(block.photos, id, $event)")
                  SelectedPhoto(:id="id", :sizes="block.photos.length > 1 ? '(max-width: 640px) 92vw, 34vw' : '(max-width: 640px) 92vw, 60vw'")
                figcaption {{ photoCaption(id) }}
        .biography-paths
          p.about-kicker {{ isZh ? '延伸閱讀' : 'Further reading' }}
          router-link.about-text-link(:to="localizedPath('/dialogues')") {{ isZh ? '對話錄：他的原話與筆記' : 'Dialogues: his own words and notes' }} ↗︎
          router-link.about-text-link(:to="localizedPath('/about/lifetree')") {{ $t('about.lifetreeLink') }} ↗︎
          router-link.about-text-link(:to="localizedPath('/about/quotes')") {{ $t('about.quotesWallLink') }} ↗︎
          router-link.about-text-link(:to="localizedPath('/thesis')") {{ $t('footer.thesis') }} ↗︎
    WorkLightbox(ref="viewer", :zh="isZh")

  section.about-chapter#about-practice(aria-labelledby="practice-title", data-about-reveal)
    .chapter-heading
      span.chapter-number 02
      h2#practice-title {{ isZh ? '創作的三個方向' : 'Three lines of inquiry' }}
    .practice-lines
      article.practice-line(v-for="practice in practices", :key="practice.id")
        .practice-image
          img(:src="practice.image", :alt="practice.work", width="1200", height="800", loading="lazy")
        .practice-body
          span.about-kicker {{ practice.eyebrow }}
          h3 {{ practice.title }}
          p {{ practice.description }}
          router-link.about-text-link(:to="localizedPath(practice.path)") {{ practice.work }} ↗︎
          router-link.practice-secondary(:to="localizedPath(practice.relatedPath)") {{ practice.relatedLabel }} →

  section.about-chapter#about-encounters(aria-labelledby="encounters-title", data-about-reveal)
    .chapter-heading
      span.chapter-number 03
      h2#encounters-title {{ isZh ? '作品走進現場' : 'In the world' }}
      router-link.about-text-link(:to="localizedPath('/work')") {{ $t('about.viewAllExhibitions') }} ↗︎
    .encounter-grid
      router-link.encounter(v-for="encounter in selectedEncounters", :key="encounter.path", :to="localizedPath(encounter.path)")
        img(:src="encounter.image", :alt="encounter.title", width="1200", height="800", loading="lazy")
        .encounter-caption
          span.about-kicker {{ encounter.year }} · {{ encounter.place }}
          h3 {{ encounter.title }}
          span(aria-hidden="true") ↗︎
    .now-editorial(v-if="nowItems.length")
      h3.about-kicker {{ $t('about.sectionNow') }}
      router-link.now-entry(v-for="item in nowItems", :key="item.label", :to="localizedPath(item.to || '/about')")
        time {{ item.date }}
        p {{ item.label }} ↗︎
        span {{ item.place }}
    .record-list
      h3.about-kicker {{ isZh ? '精選展出與駐村' : 'Selected exhibitions & residencies' }}
        span.disclosure-count {{ exhibitions.length }}
      component.record-row(v-for="exh in exhibitions", :key="exh.title", :is="exh.to ? 'router-link' : (exh.link ? 'a' : 'div')", :to="exh.to ? localizedPath(exh.to) : null", :href="exh.link || null", :target="exh.link ? '_blank' : null", :rel="exh.link ? 'noopener noreferrer' : null")
        span.record-year {{ exh.year }}
        .record-copy
          h4 {{ exh.title }}
          p {{ exh.venue }}
        span(v-if="exh.to || exh.link", aria-hidden="true") ↗︎
      router-link.about-text-link(:to="localizedPath('/cv')") {{ $t('about.viewFullHistory') }} ↗︎

  section.about-chapter#about-records(aria-labelledby="records-title", data-about-reveal)
    .chapter-heading
      span.chapter-number 04
      h2#records-title {{ isZh ? '一路留下的紀錄' : 'A continuing record' }}
      router-link.about-text-link(:to="localizedPath('/cv')") {{ $t('about.viewFullCV') }} ↗︎
    .records-intro
      p {{ $t('about.cvDesc') }}
    .record-columns
      .record-column
        h3.about-kicker {{ isZh ? '獎項與入選' : 'Awards & selections' }}
        .record-row(v-for="award in awardsMajorList", :key="award.title")
          span.record-year {{ award.year }}
          .record-copy
            h4 {{ award.title }}
            p {{ award.desc }}
        h3.about-kicker.record-subheading {{ $t('about.awardsEarly') }}
        .record-row(v-for="award in awardsEarlyList", :key="award.title")
          span.record-year {{ award.year }}
          .record-copy
            h4 {{ award.title }}
            p {{ award.desc }}
      .record-column
        h3.about-kicker {{ $t('cv.education') }}
        .career-entry(v-for="item in educationList", :key="item.title")
          h4 {{ item.title }}
          p {{ item.place }}
        h3.about-kicker.record-subheading {{ $t('about.sectionPositions') }}
        .career-entry(v-for="item in positionsList", :key="item.title")
          h4 {{ item.title }}
          p {{ item.place }}
        h3.about-kicker.record-subheading {{ isZh ? '創作與教學概覽' : 'Practice & teaching at a glance' }}
        dl.practice-facts
          template(v-for="fact in practiceFacts", :key="fact.key")
            dt {{ $t('about.' + fact.key) }}
            dd {{ fact.value }}
    details.about-disclosure
      summary {{ $t('about.sectionTalks') }}
        span.disclosure-count {{ talksList.length }}
      .record-row(v-for="talk in talksList", :key="talk.title + talk.year")
        span.record-year {{ talk.year }}
        .record-copy
          h4 {{ talk.title }}
          p {{ talk.venue }}
    details.about-disclosure
      summary {{ $t('about.sectionMilestones') }}
        span.disclosure-count {{ milestonesList.length }}
      .record-row(v-for="m in milestonesList", :key="m.year")
        span.record-year {{ m.year }}
        ul.milestone-list
          li(v-for="tag in m.tags", :key="tag.label")
            router-link(v-if="tag.url && tag.url.startsWith('/')", :to="tag.url") {{ tag.label }} ↗︎
            a(v-else-if="tag.url", :href="tag.url", target="_blank", rel="noopener noreferrer") {{ tag.label }} ↗︎
            span(v-else) {{ tag.label }}

  section.about-chapter#about-perspectives(aria-labelledby="perspectives-title", data-about-reveal)
    .chapter-heading
      span.chapter-number 05
      h2#perspectives-title {{ isZh ? '在對話之中' : 'In conversation' }}
      router-link.about-text-link(:to="localizedPath('/press')") {{ $t('about.sectionPress') }} ↗︎
    .perspective-list
      a.perspective(v-for="card in pressCardsList.slice(0, 3)", :key="card.url", :href="card.url", target="_blank", rel="noopener noreferrer")
        span.about-kicker {{ card.source }}
        .perspective-copy
          h3 {{ card.title }}
          p {{ card.desc }}
        span(aria-hidden="true") ↗︎
    details.about-disclosure(v-if="pressCardsList.length > 3 || pressMoreList.length")
      summary {{ isZh ? '更多訪談、文章與報導' : 'More interviews, essays & coverage' }}
        span.disclosure-count {{ pressCardsList.length + pressMoreList.length - 3 }}
      a.record-row(v-for="card in [...pressCardsList.slice(3), ...pressMoreList]", :key="card.url", :href="card.url", target="_blank", rel="noopener noreferrer")
        .record-copy
          span.about-kicker {{ card.source }}
          h3 {{ card.title }}
          p(v-if="card.desc") {{ card.desc }}
        span(aria-hidden="true") ↗︎

  section.about-chapter#about-open-practice(aria-labelledby="open-practice-title", data-about-reveal)
    .chapter-heading
      span.chapter-number 06
      h2#open-practice-title {{ isZh ? '向外延伸的實踐' : 'An open practice' }}
    .open-practice-list
      .open-practice-group(v-for="group in ecosystemGroups", :key="group.title")
        h3 {{ group.title }}
        .open-practice-entries
          a.open-practice-entry(v-for="entry in group.entries", :key="entry.key", :href="entry.href", target="_blank", rel="noopener noreferrer")
            h4 {{ $t('about.' + entry.key + '.name') }}
              span(aria-hidden="true") ↗︎
            p {{ $t('about.' + entry.key + '.desc') }}

  section.about-chapter#about-press-kit(aria-labelledby="press-kit-title", data-about-reveal)
    .chapter-heading
      span.chapter-number 07
      h2#press-kit-title {{ isZh ? '給媒體與策展人' : 'For press & curators' }}
    .press-kit
      .press-version
        p.about-kicker {{ isZh ? '一句話介紹' : 'One line' }}
        p.press-text {{ bio.oneLiner }}
        button.about-text-link(type="button", @click="copy('oneLiner')") {{ copied === 'oneLiner' ? (isZh ? '已複製' : 'Copied') : (isZh ? '複製這句' : 'Copy') }}
      .press-version
        p.about-kicker {{ isZh ? '短版簡介 · 約 ' + shortLength + ' 字' : 'Short biography · ' + shortLength + ' words' }}
        p.press-text {{ bio.short }}
        button.about-text-link(type="button", @click="copy('short')") {{ copied === 'short' ? (isZh ? '已複製' : 'Copied') : (isZh ? '複製短版' : 'Copy') }}
      .press-resources
        p.about-kicker {{ isZh ? '完整版與資料' : 'Full text & materials' }}
        button.about-text-link(type="button", @click="copy('long')") {{ copied === 'long' ? (isZh ? '已複製完整簡介' : 'Full biography copied') : (isZh ? '複製完整簡介（本頁第 01 章全文）' : 'Copy the full biography (section 01)') }}
        a.about-text-link(:href="isZh ? '/ai/profile.zh.md' : '/ai/profile.en.md'") {{ isZh ? '履歷版簡介（Markdown 純文字）' : 'CV biography (plain Markdown)' }} ↗︎
        router-link.about-text-link(:to="localizedPath('/cv')") {{ $t('about.viewFullCV') }} ↗︎
        router-link.about-text-link(:to="localizedPath('/cv') + '#cv-actions'") {{ isZh ? '列印履歷／儲存 PDF' : 'Print CV / Save PDF' }} ↗︎
        a.about-text-link(href="/static/img/CheYu-0401-crop.jpg", download="Che-Yu-Wu-portrait.jpg") {{ isZh ? '肖像照（JPG，900 × 1290）' : 'Portrait (JPG, 900 × 1290)' }} ↓
        router-link.about-text-link(:to="localizedPath('/press')") {{ $t('about.sectionPress') }} ↗︎
    p.copy-status(role="status", aria-live="polite") {{ copyMessage }}

  section.about-contact#about-contact(aria-labelledby="contact-title", data-about-reveal)
    .contact-prelude
      p.about-kicker {{ $t('contact.title') }}
      h2#contact-title {{ $t('about.contactDesc') }}
    .contact-destination
      a.artist-email(href="mailto:cheyu.wu@monoame.com") cheyu.wu@monoame.com ↗︎
      .contact-actions
        a.about-text-link(href="https://www.youtube.com/watch?v=E2qRrNNSSQM", target="_blank", rel="noopener noreferrer") {{ isZh ? 'MonoLab 2025 年度回顧' : 'MonoLab 2025 Annual Highlights' }} ↗︎
      .artist-elsewhere
        a(v-for="social in socialLinks", :key="social.label", :href="social.href", target="_blank", rel="noopener noreferrer") {{ social.label }} ↗︎
</template>

<script>
import { withSlash } from '@/runtime/site-path.mjs'
import SelectedPhoto from '@/components/photos/SelectedPhoto.vue'
import WorkLightbox from '@/components/work/WorkLightbox.vue'
import photos from '@/data/selected-photos.json'
import biography from '@/data/about-biography.json'
import { useI18n } from 'vue-i18n'
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useHead } from '@unhead/vue'
import { courseHref } from '@/components/teaching/course-link.mjs'

const LINK = /\[([^\]]+)\]\(([^)\s]+)\)/g
const plain = text => text.replace(LINK, '$1')

export default {
  name: 'PageAbout',
  components: { SelectedPhoto, WorkLightbox },
  setup() {
    const { locale, t } = useI18n()
    const isZh = computed(() => locale.value === 'zh')
    const bio = computed(() => biography[isZh.value ? 'zh' : 'en'])
    const aboutRoot = ref(null)
    const viewer = ref(null)
    let observer
    const currentChapter = ref('')
    let chapterObserver
    onMounted(() => {
      if (!('IntersectionObserver' in window)) return
      // 簡介目錄標出正在讀的段落
      chapterObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => { if (entry.isIntersecting) currentChapter.value = entry.target.id })
      }, { rootMargin: '-35% 0px -60% 0px' })
      aboutRoot.value?.querySelectorAll('.bio-chapter').forEach(el => chapterObserver.observe(el))
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
      observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return
          entry.target.classList.add('chapter-arrived')
          observer.unobserve(entry.target)
        })
      }, { threshold: 0.02 })
      aboutRoot.value?.querySelectorAll('[data-about-reveal]').forEach(el => observer.observe(el))
    })
    onBeforeUnmount(() => { observer?.disconnect(); chapterObserver?.disconnect(); clearTimeout(copyTimer) })

    // Function to generate localized paths
    // /zh/ 開頭（只有中文版的頁）與 /archive/（原件存檔，單一語言的靜態頁）不加語言前綴
    const localizedPath = (path) => withSlash(locale.value === 'zh' && !/^\/(zh|archive)\//.test(path) ? '/zh' + path : path)

    // 段落裡的 [文字](/路徑/) 轉成站內連結；/archive/ 是 SPA 之外的靜態頁，用一般連結整頁載入
    const segments = text => {
      const out = []
      let last = 0
      for (const m of text.matchAll(LINK)) {
        if (m.index > last) out.push({ text: text.slice(last, m.index) })
        if (/^https?:/.test(m[2])) out.push({ text: m[1], href: m[2], external: true })
        else if (m[2].startsWith('/archive/')) out.push({ text: m[1], href: m[2] })
        else out.push({ text: m[1], to: localizedPath(m[2]) })
        last = m.index + m[0].length
      }
      if (last < text.length) out.push({ text: text.slice(last) })
      return out
    }
    const photoCaption = id => photos[id]?.caption[isZh.value ? 'zh' : 'en'] || ''
    const openPhoto = (ids, id, event) => {
      viewer.value?.open(ids.map(key => ({ src: photos[key].src, alt: photoCaption(key), caption: photoCaption(key) })), ids.indexOf(id), event.currentTarget)
    }

    // 給媒體複製用的純文字：短版＝首段；完整版＝第 01 章全文（段落、引句與出處）
    const longText = computed(() => {
      const b = bio.value
      const parts = [isZh.value ? '吳哲宇 Che-Yu Wu｜藝術家簡介' : 'Che-Yu Wu 吳哲宇 | Biography', b.short]
      for (const chapter of b.chapters) {
        parts.push(chapter.title)
        for (const block of chapter.blocks) {
          if (block.p) parts.push(plain(block.p))
          if (block.quote) parts.push(isZh.value ? `「${block.quote}」（${block.cite}）` : `“${block.quote}” (${block.cite})`)
        }
      }
      parts.push(isZh.value ? `來源：https://cheyuwu.com/zh/about/（${biography.updated} 更新）` : `Source: https://cheyuwu.com/about/ (updated ${biography.updated})`)
      return parts.join('\n\n')
    })
    // 中文字數＝漢字＋每個英文詞算一個，取到十位（「約 300 字」）；英文＝詞數
    const shortLength = computed(() => {
      const text = bio.value.short
      if (!isZh.value) return text.split(/\s+/).length
      const han = (text.match(/[\u3400-\u9fff]/g) || []).length
      const words = (text.match(/[A-Za-z0-9][A-Za-z0-9.\-'’]*/g) || []).length
      return Math.round((han + words) / 10) * 10
    })
    const copied = ref('')
    const copyMessage = ref('')
    let copyTimer
    const copy = async kind => {
      const text = kind === 'long' ? longText.value : bio.value[kind]
      try {
        await navigator.clipboard.writeText(text)
      } catch {
        const area = document.createElement('textarea')
        area.value = text
        area.setAttribute('readonly', '')
        area.style.position = 'fixed'
        area.style.opacity = '0'
        document.body.appendChild(area)
        area.select()
        document.execCommand('copy')
        area.remove()
      }
      copied.value = kind
      copyMessage.value = isZh.value ? '已複製到剪貼簿' : 'Copied to clipboard'
      clearTimeout(copyTimer)
      copyTimer = setTimeout(() => { copied.value = ''; copyMessage.value = '' }, 2400)
    }

    const chapters = computed(() => [
      { id: 'about-biography', label: isZh.value ? '簡介' : 'Biography' },
      { id: 'about-practice', label: isZh.value ? '創作' : 'Practice' },
      { id: 'about-encounters', label: isZh.value ? '現場' : 'Encounters' },
      { id: 'about-records', label: isZh.value ? '紀錄' : 'Records' },
      { id: 'about-perspectives', label: isZh.value ? '對話' : 'Perspectives' },
      { id: 'about-open-practice', label: isZh.value ? '延伸' : 'Open practice' },
      { id: 'about-press-kit', label: isZh.value ? '媒體資料' : 'Press' },
    ])
    const practices = computed(() => [
      { id: 'life', eyebrow: isZh.value ? '演算法與生命' : 'Algorithms & life', title: isZh.value ? '讓規則長出形態' : 'Giving rules a living form', description: isZh.value ? '花、魚群與粒子在演算法裡生長。作品的形態，隨著系統與觀看者的相遇而改變。' : 'Flowers, shoals, and particles grow through algorithms. Their forms change as the system encounters its viewers.', image: '/static/img/unfinished-bloom-2026.jpg', work: isZh.value ? '未竟之花' : 'Unfinished Bloom', path: '/work/unfinished-bloom', relatedPath: '/nft/soulfish', relatedLabel: 'SoulFish' },
      { id: 'sound', eyebrow: isZh.value ? '音樂與現場' : 'Music & live performance', title: isZh.value ? '用聲音回應世界' : 'A world answered in sound', description: isZh.value ? '鋼琴即興、空間聲響與即時生成影像，讓演奏者與數位世界互相改變。' : 'Piano improvisation, spatial sound, and real-time imagery allow the performer and a digital world to change each other.', image: '/static/img/the-last-input/09-performance-city.jpg', work: 'The Last Input', path: '/exhibition/the-last-input', relatedPath: '/music', relatedLabel: isZh.value ? '音樂作品與演出' : 'Music & performances' },
      { id: 'public', eyebrow: isZh.value ? '共生與公共知識' : 'Symbiosis & public knowledge', title: isZh.value ? '讓創作繼續生長' : 'A practice that keeps growing', description: isZh.value ? '從 Muse 到 Taiwan.md，人與人工智慧共同維護記憶、知識與一套持續演化的規則。' : 'From Muse to Taiwan.md, people and artificial intelligence maintain memory, knowledge, and an evolving set of rules together.', image: '/static/img/taiwan-md/site-home.webp', work: 'Taiwan.md', path: '/work/taiwan-md', relatedPath: '/thinking', relatedLabel: isZh.value ? '思想與對話' : 'Thinking & dialogues' },
    ])
    const selectedEncounters = computed(() => [
      { year: '2026', title: 'The Last Input', place: 'C-LAB, Taipei', path: '/exhibition/the-last-input', image: '/static/img/the-last-input/08-performance-plants.jpg' },
      { year: '2025', title: isZh.value ? '瓶中的永恆花園' : 'The Eternal Garden in a Bottle', place: 'Pier-2, Kaohsiung', path: '/exhibitions/eternal-garden-in-a-bottle', image: 'https://s3.ap-northeast-1.amazonaws.com/strapi-files.monolab.world/prd/large_cover_17b5f78a74.jpg' },
    ])
    // 精選展出：個展只有《混沌實驗室》與《萬物公式》兩場（CV v3.4.2，2026-10-04 哲宇裁定）。有站內作品頁的一律連站內。
    const exhibitions = computed(() => isZh.value ? [
      { year: '2026', title: 'The Last Input', venue: 'C-LAB 臺灣聲響實驗室 × IRCAM 聲鬥陣進駐計畫，49.4 聲道空間聲響演出', to: '/work/the-last-input/' },
      { year: '2026', title: '《未竟之花》', venue: '新北市美術館（2026 年 4 月 24 日開展）', to: '/work/unfinished-bloom/' },
      { year: '2025', title: '《瓶中的永恆花園》沉浸互動演奏展', venue: '高雄駁二藝術特區', to: '/exhibitions/eternal-garden-in-a-bottle/' },
      { year: '2025', title: '《演算詩篇：夢境與孤寂的邊界》', venue: '華山文創園區，沉浸式鋼琴音樂會', to: '/work/algorithmic-verses/' },
      { year: '2025', title: '《咖啡幻夢》', venue: '星巴克 Dream Plaza', to: '/work/coffee-dreamscape/' },
      { year: '2024', title: '《瓶中的永恆花園》駐村創作', venue: '巴黎 CENTQUATRE-PARIS（文策院支持）', to: '/work/eternal-garden-in-a-bottle/' },
      { year: '2024', title: '《靈魂魚》', venue: 'Personal Structures，European Cultural Centre 主辦，威尼斯雙年展平行展', to: '/work/soulfish/' },
      { year: '2023', title: '《花的靈魂》', venue: '邁阿密巴塞爾藝術展，Tezos Foundation 邀請展出', to: '/work/soul-of-flowers/' },
      { year: '2023', title: '個展《萬物公式》', venue: '台北 101 AMBI SPACE ONE', to: '/work/the-great-equation/' },
      { year: '2022', title: '個展《混沌實驗室》', venue: '台北 101 AMBI SPACE ONE', to: '/work/chaos-lab/' },
    ] : [
      { year: '2026', title: 'The Last Input', venue: 'IRCAM Sound Off residency, C-LAB Taiwan Sound Lab — 49.4-channel spatial sound performance', to: '/work/the-last-input/' },
      { year: '2026', title: 'Unfinished Bloom', venue: 'New Taipei City Art Museum (opened 24 April 2026)', to: '/work/unfinished-bloom/' },
      { year: '2025', title: 'The Eternal Garden in a Bottle, immersive performance exhibition', venue: 'Pier-2 Art Center, Kaohsiung', to: '/exhibitions/eternal-garden-in-a-bottle/' },
      { year: '2025', title: 'Algorithmic Verses: The Boundary of Dreams and Solitude', venue: 'Huashan 1914 Creative Park, immersive piano concert', to: '/work/algorithmic-verses/' },
      { year: '2025', title: 'The Coffee Dreamscape', venue: 'Starbucks Dream Plaza', to: '/work/coffee-dreamscape/' },
      { year: '2024', title: 'The Eternal Garden in a Bottle, artist residency', venue: 'CENTQUATRE-PARIS (supported by TAICCA)', to: '/work/eternal-garden-in-a-bottle/' },
      { year: '2024', title: 'SoulFish', venue: 'Personal Structures, organized by the European Cultural Centre, a parallel exhibition of the Venice Biennale', to: '/work/soulfish/' },
      { year: '2023', title: 'The Soul of Flowers', venue: 'Art Basel Miami Beach, at the invitation of the Tezos Foundation', to: '/work/soul-of-flowers/' },
      { year: '2023', title: 'The Great Equation (solo)', venue: 'Taipei 101 AMBI SPACE ONE', to: '/work/the-great-equation/' },
      { year: '2022', title: 'Chaos Lab (solo)', venue: 'Taipei 101 AMBI SPACE ONE', to: '/work/chaos-lab/' },
    ])
    const ecosystemGroups = computed(() => [
      { title: isZh.value ? '共生的系統' : 'Systems of symbiosis', entries: [{ key: 'taiwanmd', href: 'https://taiwan.md/' }, { key: 'museSemiont', href: 'https://muse.cheyuwu.com' }] },
      { title: isZh.value ? '共同製作' : 'Making together', entries: [{ key: 'monolab', href: 'https://monolab.world/zh/' }] },
      { title: isZh.value ? '分享創作的方法' : 'Sharing the methods', entries: [{ key: 'tdWorkshop', href: courseHref({ slug: 'monolab', platform: 'monolab', url: 'https://education.monolab.world/' }, 'about', isZh.value ? 'zh' : 'en') }, { key: 'creativecoding', href: 'https://creativecoding.in/' }, { key: 'hahow', href: 'https://hahow.in/cr/monoame-webdesign1' }] },
    ])
    const socialLinks = [
      { label: 'Instagram', href: 'https://www.instagram.com/cheyuwu345/' },
      { label: 'StreetVoice', href: 'https://streetvoice.com/frank890417/' },
      { label: 'Spotify', href: 'https://open.spotify.com/artist/0AeZiCXhHzvexu47FOY5Tq' },
      { label: 'GitHub', href: 'https://github.com/frank890417' },
      { label: 'fxhash', href: 'https://www.fxhash.xyz/u/Che-Yu%20Wu' },
      { label: 'OpenProcessing', href: 'https://openprocessing.org/user/139364/' },
    ]
    const practiceFacts = [
      { key: 'studentsCount', value: '23,000+' }, { key: 'exhibitionsCount', value: '45+' },
      { key: 'artworksCount', value: '400+' }, { key: 'tracksCount', value: '59+' }, { key: 'talksCount', value: '40+' },
    ]

    // SEO: canonical／hreflang／og 由 App.vue 依路由統一注入（2026-09-05），本頁不再自帶。
    // GEO: About 是 ProfilePage，主實體＝index.html 全站 JSON-LD 裡的 #person，這裡只引用 @id（2026-10-15 backlog P04：
    // 完整的 Person 只有那一份，連同生日、母校、sameAs、公司 founder 都在 index.html；出處與測試 → scripts/test-person-schema.mjs）。
    useHead({
      script: computed(() => [{
        key: 'about-profile-jsonld',
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ProfilePage',
          dateModified: biography.updated,
          url: isZh.value ? 'https://cheyuwu.com/zh/about/' : 'https://cheyuwu.com/about/',
          inLanguage: isZh.value ? 'zh-Hant-TW' : 'en',
          // 完整的 Person 只在 index.html 的全站 JSON-LD（每一頁的殼都帶它）；這裡只引用 @id，不再宣告第二份
          mainEntity: { '@id': 'https://cheyuwu.com/#person' },
        }),
      }]),
    })

    return {
      localizedPath, isZh, bio, aboutRoot, viewer, currentChapter, segments, photoCaption, openPhoto, copy, copied, copyMessage, shortLength,
      chapters, practices, selectedEncounters, exhibitions, ecosystemGroups, socialLinks, practiceFacts,
    }
  },
  computed: {
    nowItems() { return this.$tm('about.nowItems') || [] },
    awardsMajorList() { return this.$tm('about.awardsMajorList') || [] },
    awardsEarlyList() { return this.$tm('about.awardsEarlyList') || [] },
    pressCardsList() { return this.$tm('about.pressCards') || [] },
    pressMoreList() { return this.$tm('about.pressMore') || [] },
    talksList() { return this.$tm('about.talks') || [] },
    educationList() { return this.$tm('about.careerEducation') || [] },
    positionsList() { return this.$tm('about.careerPositions') || [] },
    milestonesList() { return this.$tm('about.milestones') || [] }
  },
}
</script>
<style lang="sass">
.about-v2
  --about-paper: #f2eee5
  --about-muted: #b8b5ad
  --about-subtle: #99968f
  --about-rule: rgba(242,238,229,0.2)
  --about-gap: clamp(64px, 8.5vw, 144px)
  background: #080808
  color: var(--about-paper)
  text-align: left
  padding-bottom: 32px
  font-family: var(--font-body)
  > header, > section, > .about-chapter-nav
    width: min(90vw, 1640px)
    margin-left: auto
    margin-right: auto
  a
    color: inherit
    text-decoration: none
    &:hover
      color: var(--gold)
    &:focus-visible
      outline: 2px solid var(--gold)
      outline-offset: 5px
  button
    font: inherit
    color: inherit
    &:focus-visible
      outline: 2px solid var(--gold)
      outline-offset: 5px
  h1, h2, h3, h4, p, figure, blockquote, dl, dd
    margin: 0
  h1, h2, h3
    font-family: var(--font-slab)
    font-weight: 400
  &.about-zh h2, &.about-zh h3
    font-family: var(--font-display)
  .about-kicker
    display: block
    color: var(--about-subtle)
    font-size: 0.75rem
    font-weight: 500
    line-height: 1.65
    letter-spacing: 0.11em
    text-transform: uppercase
  .about-text-link
    display: inline-block
    padding: 10px 0
    background: none
    border: 0
    border-bottom: 1px solid var(--about-rule)
    border-radius: 0
    font-size: 0.87rem
    line-height: 1.65
    min-height: 44px
    text-align: left
    cursor: pointer
    transition: var(--t-hover)
    &:hover
      color: var(--gold)
      border-color: var(--gold)

  // ── 開場：名字、一句話、速覽、肖像 ──
  .portrait-opening
    padding-top: clamp(40px, 5vw, 80px)
    display: grid
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.8fr)
    column-gap: 7vw
    row-gap: 40px
    align-items: start
  .portrait-identity
    grid-column: 1
    grid-row: 1
    padding-top: 8px
    h1
      margin: 22px 0 22px
      font-size: clamp(3.25rem, 7.4vw, 7.4rem)
      line-height: 0.94
      letter-spacing: -0.045em
    .name-en, .name-zh
      display: block
      white-space: nowrap
    .name-zh
      font-family: var(--font-display)
      font-size: 0.53em
      line-height: 1.3
      letter-spacing: 0.08em
      margin-top: 16px
  .identity-line
    max-width: 40em
    text-wrap: pretty
    font-size: clamp(1.06rem, 1.35vw, 1.3rem)
    line-height: 1.8
    color: var(--about-paper)
  &.about-zh .identity-line
    max-width: 34em
    line-height: 1.9
  .at-a-glance
    margin-top: 30px
    border-top: 1px solid var(--about-rule)
    max-width: 760px
    .glance-row
      display: grid
      grid-template-columns: 8.5em minmax(0, 1fr)
      gap: 16px
      padding: 11px 0
      border-bottom: 1px solid rgba(242,238,229,0.09)
      font-size: 0.9rem
      line-height: 1.7
    dt
      color: var(--about-subtle)
      font-size: 0.8rem
      padding-top: 1px
    dd
      color: var(--about-muted)
    .glance-item
      display: inline
    .glance-sep
      color: var(--about-subtle)
      white-space: pre
    a
      color: var(--about-paper)
      border-bottom: 1px solid var(--about-rule)
      &:hover
        color: var(--gold)
        border-color: var(--gold)
  .opening-links
    display: flex
    flex-wrap: wrap
    gap: 4px 28px
    margin-top: 22px
  .portrait-image
    grid-column: 2
    grid-row: 1 / span 2
    width: 100%
    align-self: stretch
    img
      display: block
      width: 100%
      height: 100%
      max-height: 860px
      min-height: 520px
      aspect-ratio: 3 / 4
      object-fit: cover
      object-position: 50% 34%
  .opening-statement
    grid-column: 1
    margin-top: 4px
    max-width: 100%
    p
      max-width: 24ch
      font-family: var(--font-display)
      font-size: clamp(1.6rem, 2.4vw, 2.5rem)
      line-height: 1.55
      letter-spacing: -0.01em
    cite
      display: block
      font-family: var(--font-body)
      font-size: 0.75rem
      line-height: 1.6
      font-style: normal
      color: var(--about-subtle)
      margin-top: 16px
  .about-chapter-nav
    display: flex
    flex-wrap: wrap
    gap: 4px 36px
    margin-top: 56px
    padding: 20px 0
    border-top: 1px solid var(--about-rule)
    border-bottom: 1px solid var(--about-rule)
    a
      display: inline-flex
      align-items: baseline
      gap: 12px
      min-height: 44px
      padding: 12px 0
      font-size: 0.85rem
      span
        color: var(--about-subtle)
        font-variant-numeric: tabular-nums
        font-size: 0.67rem
  .about-chapter
    padding-top: var(--about-gap)
    scroll-margin-top: 84px
  .chapter-heading
    display: flex
    align-items: baseline
    flex-wrap: wrap
    gap: 18px 26px
    margin-bottom: 44px
    h2
      flex: 1
      font-size: clamp(2rem, 3.8vw, 3.8rem)
      line-height: 1.25
      letter-spacing: -0.02em
      min-width: 0
    .chapter-number
      color: var(--about-subtle)
      font-size: 0.75rem
      font-variant-numeric: tabular-nums
      min-width: 24px

  // ── 01 藝術家簡介：本頁主體，長文閱讀版面 ──
  .biography-layout
    display: grid
    grid-template-columns: minmax(200px, 0.55fr) minmax(0, 1.45fr)
    gap: 7vw
    align-items: start
  .biography-margin
    position: sticky
    top: 96px
    border-top: 1px solid var(--about-rule)
    padding-top: 22px
  .bio-toc
    display: flex
    flex-direction: column
    align-items: stretch
    text-align: left
    a
      display: grid
      grid-template-columns: 5.6em minmax(0, 1fr)
      gap: 12px
      padding: 10px 0
      min-height: 44px
      font-size: 0.9rem
      line-height: 1.6
      border-bottom: 1px solid rgba(242,238,229,0.09)
    .toc-years
      color: var(--about-subtle)
      font-size: 0.75rem
      font-variant-numeric: tabular-nums
      padding-top: 2px
    a.is-current
      color: var(--gold)
      .toc-years
        color: var(--gold)
  .bio-copy-actions
    display: flex
    flex-direction: column
    align-items: flex-start
    gap: 2px
    margin-top: 36px
    .about-kicker
      margin-bottom: 4px
  .biography-reading
    min-width: 0
    max-width: 42em
    p
      font-size: clamp(1.03rem, 1.3vw, 1.2rem)
      line-height: 1.9
      color: var(--about-muted)
      margin: 0 0 1.25em
    a
      color: var(--about-paper)
      border-bottom: 1px solid var(--about-rule)
      &:hover
        border-color: var(--gold)
    .bio-lead
      font-size: clamp(1.18rem, 1.7vw, 1.55rem)
      line-height: 1.8
      color: var(--about-paper)
      margin-bottom: 1.6em
  &.about-zh .biography-reading
    max-width: 40em
    p
      line-height: 2
      letter-spacing: 0.02em
    .bio-lead
      line-height: 1.95
  .bio-chapter
    padding-top: 56px
    scroll-margin-top: 96px
  .bio-chapter-head
    display: flex
    flex-direction: column
    gap: 8px
    margin-bottom: 26px
    padding-top: 22px
    border-top: 1px solid var(--about-rule)
    h3
      font-size: clamp(1.6rem, 2.3vw, 2.35rem)
      line-height: 1.35
      letter-spacing: -0.01em
  .bio-quote
    margin: 2.1em 0 2.3em
    padding-left: clamp(18px, 2.2vw, 32px)
    border-left: 1px solid var(--gold)
    p
      font-family: var(--font-display)
      font-size: clamp(1.15rem, 1.6vw, 1.45rem)
      line-height: 1.8
      color: var(--about-paper)
      margin: 0
    cite
      display: block
      margin-top: 12px
      font-size: 0.78rem
      line-height: 1.6
      font-style: normal
      color: var(--about-subtle)
      a
        color: inherit
  .bio-figures
    display: grid
    gap: 20px
    margin: 2.4em 0 2.6em
    &.count-2
      grid-template-columns: 1fr 1fr
    figure
      min-width: 0
    button
      display: block
      width: 100%
      padding: 0
      border: 0
      background: transparent
      cursor: zoom-in
    img
      display: block
      width: 100%
      height: auto
      aspect-ratio: 3 / 2
      object-fit: cover
    figcaption
      margin-top: 10px
      font-size: 0.8rem
      line-height: 1.6
      color: var(--about-subtle)
  .biography-paths
    display: flex
    flex-wrap: wrap
    gap: 4px 26px
    margin-top: 64px
    padding-top: 22px
    border-top: 1px solid var(--about-rule)
    .about-kicker
      flex-basis: 100%
    a
      border-bottom: 1px solid var(--about-rule)
      color: var(--about-paper)

  // ── 02 創作的三個方向 ──
  .practice-lines
    display: grid
    grid-template-columns: repeat(3, minmax(0, 1fr))
    gap: 36px
  .practice-image
    overflow: hidden
    margin-bottom: 24px
    img
      display: block
      width: 100%
      height: auto
      aspect-ratio: 4 / 3
      object-fit: cover
  .practice-body
    h3
      font-size: clamp(1.55rem, 2.1vw, 2.25rem)
      line-height: 1.35
      margin: 12px 0 18px
    p
      font-size: 0.96rem
      line-height: 1.85
      color: var(--about-muted)
      margin-bottom: 18px
    .practice-secondary
      display: block
      color: var(--about-subtle)
      font-size: 0.84rem
      padding-top: 16px
      min-height: 44px

  // ── 03 作品走進現場 ──
  .encounter-grid
    display: grid
    grid-template-columns: 1.1fr 0.9fr
    gap: 5vw
    align-items: start
    .encounter:nth-child(2)
      margin-top: 80px
  .encounter
    img
      display: block
      width: 100%
      height: auto
      aspect-ratio: 3 / 2
      object-fit: cover
    .encounter-caption
      position: relative
      padding: 22px 28px 0 0
      h3
        font-size: 1.75rem
        line-height: 1.4
        margin-top: 8px
      > span:last-child
        position: absolute
        right: 0
        top: 22px
  .now-editorial
    margin: 52px 0 24px
    border-top: 1px solid var(--about-rule)
    padding-top: 24px
  .now-entry
    display: grid
    grid-template-columns: 150px minmax(0, 1fr) 160px
    gap: 20px
    padding: 16px 0
    font-size: 0.92rem
    line-height: 1.7
    border-bottom: 1px solid rgba(242,238,229,0.09)
    &:hover p
      color: var(--gold)
    time, > span
      color: var(--about-subtle)
    > span
      text-align: right
  .record-list
    margin-top: 48px
    border-top: 1px solid var(--about-rule)
    padding-top: 24px
    > h3
      margin-bottom: 6px
    > .about-text-link
      margin-top: 20px
  .disclosure-count
    display: inline-block
    color: var(--about-subtle)
    font-size: 0.75rem
    margin-left: 12px
    font-variant-numeric: tabular-nums
    letter-spacing: 0
  .about-disclosure
    border-top: 1px solid var(--about-rule)
    border-bottom: 1px solid var(--about-rule)
    margin-bottom: -1px
    padding: 0
    > summary
      cursor: pointer
      font-size: 1rem
      padding: 22px 36px 22px 0
      line-height: 1.6
      position: relative
      list-style: none
      &::-webkit-details-marker
        display: none
      &::after
        content: '+'
        position: absolute
        right: 4px
        top: 22px
        color: var(--about-muted)
      &:hover
        color: var(--gold)
      &:focus-visible
        outline: 2px solid var(--gold)
        outline-offset: 2px
    &[open]
      padding-bottom: 28px
      > summary::after
        content: '−'
  .record-row
    display: flex
    gap: 28px
    padding: 18px 0
    border-bottom: 1px solid rgba(242,238,229,0.09)
    line-height: 1.7
    .record-year
      flex: 0 0 54px
      color: var(--about-subtle)
      font-size: 0.85rem
    .record-copy
      flex: 1
      min-width: 0
    h3, h4
      font-family: var(--font-body)
      font-size: 1rem
      line-height: 1.6
      font-weight: 500
    p
      font-size: 0.92rem
      color: var(--about-muted)
      margin-top: 4px
  a.record-row:hover h4, a.record-row:hover h3
    color: var(--gold)

  // ── 04 紀錄 ──
  .records-intro
    color: var(--about-muted)
    line-height: 1.8
    margin-bottom: 28px
  .record-columns
    display: grid
    grid-template-columns: 1fr 1fr
    gap: 5vw
    margin-bottom: 40px
    .record-column > .about-kicker
      padding-bottom: 6px
      border-bottom: 1px solid var(--about-rule)
  .career-entry
    padding: 16px 0
    border-bottom: 1px solid rgba(242,238,229,0.09)
    h4
      font-size: 1rem
      line-height: 1.6
      font-weight: 500
    p
      font-size: 0.92rem
      line-height: 1.7
      color: var(--about-muted)
      margin-top: 4px
  .record-subheading
    margin-top: 40px
  .milestone-list
    min-width: 0
    padding-left: 20px
    margin: 0
    color: var(--about-muted)
    li
      padding: 4px 0
      line-height: 1.8
      font-size: 0.93rem
    a
      border-bottom: 1px solid var(--about-rule)
  .practice-facts
    display: grid
    grid-template-columns: minmax(0, 1fr) auto
    gap: 0 28px
    max-width: 600px
    dt, dd
      margin: 0
      padding: 10px 0
      font-size: 0.95rem
      font-weight: 400
      line-height: 1.7
      border-bottom: 1px solid rgba(242,238,229,0.09)
    dt
      color: var(--about-muted)
    dd
      font-variant-numeric: tabular-nums

  // ── 05 對話、06 延伸 ──
  .perspective
    display: grid
    grid-template-columns: minmax(0, 0.65fr) minmax(0, 1.35fr) 20px
    gap: 5vw
    padding: 28px 0
    border-top: 1px solid var(--about-rule)
    h3
      font-size: clamp(1.45rem, 2.1vw, 2.15rem)
      line-height: 1.5
    p
      margin-top: 12px
      line-height: 1.8
      font-size: 0.95rem
      color: var(--about-muted)
  .open-practice-group
    display: grid
    grid-template-columns: minmax(0, 0.65fr) minmax(0, 1.35fr)
    gap: 5vw
    padding: 32px 0
    border-top: 1px solid var(--about-rule)
    > h3
      font-size: clamp(1.5rem, 2.5vw, 2.5rem)
      line-height: 1.4
  .open-practice-entry
    display: block
    padding: 0 0 28px
    margin-bottom: 28px
    border-bottom: 1px solid rgba(242,238,229,0.09)
    &:last-child
      padding-bottom: 0
      margin-bottom: 0
      border-bottom: 0
    h4
      display: flex
      justify-content: space-between
      gap: 16px
      font-size: 1.1rem
      font-weight: 500
      line-height: 1.6
    p
      color: var(--about-muted)
      margin-top: 10px
      font-size: 0.96rem
      line-height: 1.8
      max-width: 65ch

  // ── 07 給媒體與策展人 ──
  .press-kit
    display: grid
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.4fr) minmax(0, 0.8fr)
    gap: 4vw
    align-items: start
  .press-version, .press-resources
    border-top: 1px solid var(--about-rule)
    padding-top: 20px
    min-width: 0
  .press-text
    margin: 14px 0 12px
    font-size: 1rem
    line-height: 1.85
    color: var(--about-paper)
    user-select: text
  &.about-zh .press-text
    line-height: 1.95
  .press-resources
    display: flex
    flex-direction: column
    align-items: flex-start
    gap: 2px
    .about-kicker
      margin-bottom: 8px
  .copy-status
    min-height: 1.6em
    margin-top: 16px
    font-size: 0.8rem
    color: var(--gold)

  // ── 聯絡 ──
  .contact-actions
    display: flex
    flex-wrap: wrap
    gap: 12px 26px
  .about-contact
    margin-top: var(--about-gap)
    padding: 48px 0 32px
    border-top: 1px solid var(--about-rule)
    scroll-margin-top: 84px
    display: grid
    grid-template-columns: minmax(0, 0.65fr) minmax(0, 1.35fr)
    gap: 5vw
    h2
      font-size: clamp(1.7rem, 2.5vw, 2.7rem)
      line-height: 1.5
      margin-top: 18px
    .artist-email
      display: inline-block
      font-family: var(--font-slab)
      font-size: clamp(1.5rem, 3vw, 3rem)
      line-height: 1.4
      padding-bottom: 12px
      overflow-wrap: anywhere
    .artist-elsewhere
      display: flex
      flex-wrap: wrap
      gap: 14px 26px
      padding-top: 30px
      a
        font-size: 0.84rem
        color: var(--about-subtle)
        min-height: 44px
        padding: 10px 0
  .chapter-arrived
    animation: about-arrive 600ms ease-out both
  @keyframes about-arrive
    from
      transform: translateY(8px)
    to
      transform: translateY(0)
  @media (prefers-reduced-motion: reduce)
    .chapter-arrived
      animation: none
    a
      transition: none

  @media (max-width: 1100px)
    .press-kit
      grid-template-columns: 1fr 1fr
      .press-version:nth-child(2)
        grid-column: 1 / -1
        grid-row: 1
  @media (max-width: 1000px)
    .portrait-opening
      column-gap: 5vw
      row-gap: 26px
    .portrait-image img
      min-height: 440px
      max-height: 680px
    .practice-lines
      gap: 24px
    .practice-image img
      aspect-ratio: 1 / 1
    .biography-layout
      grid-template-columns: minmax(0, 1fr)
      gap: 32px
    .biography-margin
      position: static
      display: grid
      grid-template-columns: 1.4fr 1fr
      gap: 32px
    .bio-copy-actions
      margin-top: 0
    .chapter-heading h2
      font-size: 2.2rem
  @media (max-width: 640px)
    .portrait-opening
      display: flex
      flex-direction: column
      padding-top: 32px
      gap: 28px
    .portrait-identity
      width: 100%
      h1
        font-size: clamp(3rem, 13vw, 4.4rem)
        margin-top: 16px
        margin-bottom: 18px
      .name-zh
        display: inline-block
        margin-top: 12px
    .identity-line
      font-size: 1.02rem
    .at-a-glance
      .glance-row
        grid-template-columns: 1fr
        gap: 2px
        padding: 10px 0
    .opening-links
      margin-top: 10px
      gap: 0 22px
    .portrait-image
      order: 1
      width: 100%
      img
        min-height: 0
        max-height: none
        height: 340px
        aspect-ratio: auto
        object-position: 50% 29%
    .opening-statement
      order: 2
      margin-top: 4px
      p
        font-size: 1.55rem
    .about-chapter-nav
      gap: 0 24px
      margin-top: 40px
      a
        font-size: 0.8rem
    .chapter-heading
      gap: 12px
      margin-bottom: 28px
      h2
        flex-basis: calc(100% - 40px)
        font-size: 1.85rem
      > a
        margin-left: 36px
    .biography-margin
      grid-template-columns: 1fr
      gap: 24px
    .bio-chapter
      padding-top: 40px
    .bio-figures.count-2
      grid-template-columns: 1fr
    .practice-lines
      grid-template-columns: 1fr
      gap: 40px
    .practice-image img
      aspect-ratio: 3 / 2
    .practice-body h3
      font-size: 1.7rem
    .record-columns, .about-contact, .press-kit
      grid-template-columns: 1fr
      gap: 32px
    .press-kit .press-version:nth-child(2)
      grid-row: auto
    .encounter-grid
      grid-template-columns: 1fr
      gap: 36px
      .encounter:nth-child(2)
        margin-top: 0
    .now-entry
      grid-template-columns: 1fr
      gap: 4px
      > span
        text-align: left
    .perspective
      grid-template-columns: minmax(0, 1fr) 20px
      gap: 12px
      > .about-kicker
        grid-column: 1 / -1
      h3
        font-size: 1.5rem
    .open-practice-group
      grid-template-columns: 1fr
      gap: 24px
    .record-row
      gap: 16px
      .record-year
        flex-basis: 42px
    .about-contact .artist-email
      font-size: 1.6rem
</style>
